import { ReactElement } from "react";
import { createEnumParam } from "use-query-params";

import {
  UserRoleFilter,
  UserSubaccountRole,
  UserTenantRole,
} from "~frontend/__generated__/graphql";
import { TableControls } from "~frontend/components/Table";

import {
  SUBACCOUNT_ROLE_DISPLAY_NAMES,
  TENANT_ROLE_DISPLAY_NAMES,
  USER_INVITE_SORTS,
  USER_SORTS,
} from "./constants";
import { UserStatus } from "./types";

export type RoleFilterValue = "ALL" | UserRoleFilter;

export const ROLE_FILTER_PARAM = createEnumParam<RoleFilterValue>([
  "ALL",
  ...Object.values(UserRoleFilter),
]);

const ROLE_FILTER_OPTIONS: { value: RoleFilterValue; label: string }[] = [
  { value: "ALL", label: "All" },
  {
    value: UserRoleFilter.Owner,
    label: TENANT_ROLE_DISPLAY_NAMES[UserTenantRole.Owner],
  },
  {
    value: UserRoleFilter.Admin,
    label: SUBACCOUNT_ROLE_DISPLAY_NAMES[UserSubaccountRole.Admin],
  },
  {
    value: UserRoleFilter.Supervolunteer,
    label: SUBACCOUNT_ROLE_DISPLAY_NAMES[UserSubaccountRole.Supervolunteer],
  },
  {
    value: UserRoleFilter.Texter,
    label: SUBACCOUNT_ROLE_DISPLAY_NAMES[UserSubaccountRole.Texter],
  },
];

export default function UserListControls({
  statusFilter,
  setStatusFilter,
  roleFilter,
  setRoleFilter,
  sort,
  setSort,
  search,
  setSearch,
  onClickInvite,
  onClickGetInviteLink,
}: {
  statusFilter: UserStatus;
  setStatusFilter: (newStatus: UserStatus) => void;
  roleFilter: RoleFilterValue;
  setRoleFilter: (newRole: RoleFilterValue) => void;
  sort: string;
  setSort: (newSort: string) => void;
  search: string;
  setSearch: (newSearch: string) => void;
  onClickInvite: () => void;
  onClickGetInviteLink?: () => void;
}): ReactElement {
  return (
    <TableControls
      addButtons={[
        ...(onClickGetInviteLink
          ? [
              {
                label: "Get Invite Link",
                id: "get-invite-link",
                icon: <></>,
                onClick: onClickGetInviteLink,
              },
            ]
          : []),
        { label: "Invite User", id: "invite-user", onClick: onClickInvite },
      ]}
      filters={[
        {
          id: "status",
          label: "User Status",
          value: statusFilter,
          onChange: (value: string) => setStatusFilter(value as UserStatus),
          options: [
            { value: UserStatus.INVITED, label: "Invited" },
            { value: UserStatus.ACTIVE, label: "Active" },
          ],
        },
        {
          id: "role",
          label: "User Role",
          value: roleFilter,
          onChange: (value: string) => setRoleFilter(value as RoleFilterValue),
          options: ROLE_FILTER_OPTIONS,
        },
      ]}
      sort={{
        value: sort,
        onChange: setSort,
        options:
          statusFilter === UserStatus.INVITED ? USER_INVITE_SORTS : USER_SORTS,
      }}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search for a user...",
      }}
    />
  );
}
